import { Chain } from "@rainbow-me/rainbowkit";
import * as Sentry from "@sentry/react";
import { BigNumber, Contract, ethers } from "ethers";
import { computed, makeObservable, observable, action, reaction, runInAction, override } from "mobx";
import { DOG20, PX } from "../../../hardhat/types";

import deployedContracts from "../contracts/abi.json";
import {
  LEGACY_PX_ABI,
  CHAIN_IDS,
  NETWORK_NAMES,
  getV1ContractAddress,
  getV2ContractAddress,
  getV1ChainId,
  getV2ChainId,
} from "../contracts/legacyContracts";
import env from "../environment";
import { ObjectKeys } from "../helpers/objects";
import { abbreviate } from "../helpers/strings";
import KobosuJson from "../images/kobosu.json";
import { PixelOwnerInfo } from "../pages/Leaderbork/Leaderbork.store";
import { Http, throttledGet } from "../services";
import LocalStorage from "../services/local-storage";
import { Reactionable } from "../services/mixins/reactionable";
import CowStore from "./cow.store";
import Web3providerStore, { EthersContractError } from "./web3provider.store";

const VIEWED_PIXELS_LS_KEY = "viewed_pixels_by_id";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const log = (..._: any[]) => {};

interface AddressToPuppers {
  [k: string]: {
    tokenIds: number[];
    ens: string | null;
    ud: string | null;
  };
}

class Web3Store extends Reactionable(Web3providerStore) {
  provider: ethers.providers.Web3Provider;
  address: string | null = null;
  ens: string | null = null;

  D20_PRECISION = BigNumber.from("1000000000000000000");
  DOG_TO_PIXEL_SATOSHIS = BigNumber.from("55239898990000000000000");
  PIXEL_TO_ID_OFFSET = 1000000;
  WIDTH = 640;
  HEIGHT = 480;
  DOG_BURN_FEES_PERCENT = 1;
  targetChainId = env.app.targetChainId;
  targetNetworkName = env.app.targetNetworkName;

  @observable
  isIntentionalNetworkSwitch = false;

  @observable
  dogBalance: BigNumber | null = null;
  @action
  setDogBalance(balance: BigNumber) {
    this.dogBalance = balance;
  }

  @observable
  pupperBalance?: number;
  @action
  setPupperBalance(balance: number) {
    this.pupperBalance = balance;
  }

  @observable
  dogContract?: DOG20;

  @observable
  pxContract?: PX;

  @observable
  addressToPuppers?: AddressToPuppers;

  @observable
  pxContractAddress: string;

  @observable
  dogContractAddress: string;

  @observable
  cowStore: CowStore;

  @observable
  usdPerPixel?: number;

  @computed get dogBalanceHumanReadable() {
    return this.dogBalance ? ethers.utils.formatEther(this.dogBalance) : null;
  }

  constructor() {
    super();
    // log("Deployed Contracts:", JSON.stringify(deployedContracts, null, 2));
    // log("Target Chain ID:", this.targetChainId.toString());
    // log("Target Network Name:", this.targetNetworkName);
    log("Specific Network Contract Data:", deployedContracts[this.targetChainId.toString()]?.[this.targetNetworkName]);

    makeObservable(this);

    // reaction(
    //   () => this.signer,
    //   (signer) => {
    //     if (signer) {
    //       log("Signer changed:", signer);
    //       this.connectToContracts(signer);
    //     }
    //   }
    // );

    // makeObservable(this);
    this.addressToPuppers = {};
    this.cowStore = new CowStore();

    // safer contract access
    const chainIdStr = this.targetChainId.toString();
    const chainData = deployedContracts[chainIdStr];
    log(`Looking for contracts: chainId=${chainIdStr}, networkName=${this.targetNetworkName}`);
    log(`Available chains in abi.json:`, Object.keys(deployedContracts));

    if (chainData && chainData[this.targetNetworkName] && chainData[this.targetNetworkName].contracts) {
      this.pxContractAddress = chainData[this.targetNetworkName].contracts["PX"]?.address || "";
      this.dogContractAddress = chainData[this.targetNetworkName].contracts["DOG20"]?.address || "";
      log(`Found contracts: PX=${this.pxContractAddress}, DOG20=${this.dogContractAddress}`);
    } else {
      console.error(`Contract addresses not found for chain ${chainIdStr} / ${this.targetNetworkName}`);
      if (chainData) {
        console.error(`Available networks for chain ${chainIdStr}:`, Object.keys(chainData));
      }
    }

    this.initializeProvider();
    this.init();
    this.connectToContracts(this.provider);
  }

  initializeProvider() {
    return super.initializeProvider();
  }

  @action
  setAddress(newAddress: string) {
    // log("Setting new address:", newAddress);
    if (newAddress !== this.address) {
      this.address = newAddress;
      this.refreshSigner();
    }
  }

  refreshSigner() {
    if (this.provider) {
      this.signer = this.provider.getSigner();
      // log("Signer refreshed:", this.signer);
    }
  }

  // @action
  // setSigner(newSigner: ethers.Signer | null) {
  //   log("Setting new signer:", newSigner);
  //   if (newSigner !== this.signer) {
  //     this.signer = newSigner;
  //   }
  // }

  @override
  get addressForDisplay() {
    return this.abbreviateAddress(this.address);
  }

  abbreviateAddress(address: string | null) {
    if (!address) return "-";
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  }

  private _initialized = false;

  async init() {
    // Guard against double init (constructor calls init, and App.store.init also calls it)
    if (this._initialized) {
      log('🚀 Web3Store.init() skipped - already initialized');
      return;
    }
    this._initialized = true;

    log('🚀 Web3Store.init() called');
    try {
      await this.getPixelOwnershipMap();
      log('✅ Pixel ownership map loaded');
    } catch (error) {
      console.error('❌ Failed to load pixel ownership map:', error);
    }
    this.getShibaDimensions();
    // this.getUSDPerPixel();
  }

  async connect(signer: ethers.Signer, network: Chain, provider: ethers.providers.BaseProvider, transport?: any) {
    try {
      await super.connect(signer, network, provider, transport);

      // Only rebind contracts when on the target chain.
      // When temporarily on L1 (e.g. Sepolia for V1 burn), keep existing
      // PX/DOG20 contract bindings so reads against Base still work.
      if (network.id === this.targetChainId) {
        this.connectToContracts(this.signer!);
        this.refreshDogBalance();
        this.refreshPupperBalance();
        await this.debugContractAddresses();
        await this.errorGuardContracts();
        this.validateMyPixelsOnChain(); // fire-and-forget: prune stale/burned pixels
        this.cowStore.connect(this.signer!);
      }
    } catch (e) {
      console.error(e);
      Sentry.captureException(e);
      console.error("Error connecting");
    }
  }

  connectToContracts(signer: any) {
    const _warn = console.warn;
    console.warn = () => {};

    const px = new Contract(
      this.pxContractAddress,
      deployedContracts[this.targetChainId.toString()][this.targetNetworkName]["contracts"]["PX"].abi,
      signer,
    ) as unknown;
    this.pxContract = px as PX;

    const dog = new Contract(
      this.dogContractAddress,
      deployedContracts[this.targetChainId.toString()][this.targetNetworkName]["contracts"]["DOG20"].abi,
      signer,
    ) as unknown;
    this.dogContract = dog as DOG20;

    console.warn = _warn;

    // log("Contracts initialized:", { PX: this.pxContract, DOG20: this.dogContract });

    //@ts-ignore
    window.__PX__ = px;
    //@ts-ignore
    window.__DOG20__ = dog;
  }

  async debugContractAddresses() {
    const res = await throttledGet("/v1/contract/addresses");
    const { dog: dogAddress, pixel: pixelAddress } = res.data;

    if (dogAddress !== this.dogContractAddress) {
      throw Error(`Frontend (${this.dogContractAddress}) and API (${dogAddress}) DOG addresses do not match`);
    }

    if (pixelAddress !== this.pxContractAddress) {
      throw Error(`Frontend (${this.pxContractAddress}) and API (${pixelAddress}) PIXEL addresses do not match`);
    }

    log(`api connected to pixel contract: ${pixelAddress}`);
    log(`frontend connected to pixel contract: ${this.pxContractAddress}`);

    log(`api connected to DOG contract: ${dogAddress}`);
    log(`frontend connected to DOG contract: ${this.dogContractAddress}`);
  }

  async errorGuardContracts() {
    const nonContractCode = "0x";
    const pxCode = await this.provider.getCode(this.pxContractAddress);
    if (pxCode === nonContractCode) {
      await this.disconnect();
      throw Error(
        `PX address is not a contract, please make sure it is deployed & you are on the correct network. Got ${pxCode} ${this.network?.name} ${this.pxContractAddress}`,
      );
    }
    const dogCode = await this.provider.getCode(this.dogContractAddress);
    if (dogCode === nonContractCode) {
      await this.disconnect();
      throw Error("DOG20 address is not a contract, please make sure it is deployed & you are on the correct network.");
    }
  }

  async getPixelOwnershipMap() {
    try {
      const { data } = await throttledGet("/v1/config");
      this.addressToPuppers = data;
      return data;
    } catch (error) {
      throw error;
    }
  }

  refreshPixelOwnershipMap() {
    return Http.get("/v1/config/refresh").then(({ data }) => {
      this.addressToPuppers = data;
      this.validateMyPixelsOnChain(); // fire-and-forget
      return data;
    });
  }

  syncPixelOwnershipMap() {
    return Http.get("/v1/config/sync").then(({ data }) => {
      this.addressToPuppers = data;
      return data;
    });
  }

  async validateMyPixelsOnChain() {
    if (!this.address || !this.addressToPuppers || !this.pxContract) return;
    const myPixels = this.puppersOwned;
    if (myPixels.length === 0) return;

    const verified = await this.getOwnedEligibleV3Tokens(myPixels, this.address);
    const stale = myPixels.filter(p => !verified.includes(p));

    if (stale.length > 0) {
      console.warn(`Stale pixels removed (burned on-chain): ${stale.join(', ')}`);
      const key = Object.keys(this.addressToPuppers!).find(
        k => k.toLowerCase() === this.address!.toLowerCase()
      );
      if (key) {
        runInAction(() => {
          this.addressToPuppers![key].tokenIds = verified;
        });
      }
      // Trigger server refresh in background so future loads are correct
      Http.get("/v1/config/refresh").catch(() => {});
    }
  }

  getShibaDimensions() {
    return throttledGet("/v1/px/dimensions").then(({ data }) => {
      this.WIDTH = data.width;
      this.HEIGHT = data.height;
    });
  }

  @computed
  get puppersOwned() {
    let myPuppers: number[] = [];
    if (this.address && this.addressToPuppers) {
      const lowerAddress = this.address.toLowerCase();
      // Normalize key lookup to handle mixed-case address inconsistency between
      // wagmi (checksummed) and server DB (as-is from ethers event args)
      const matchKey = Object.keys(this.addressToPuppers).find(
        k => k.toLowerCase() === lowerAddress
      );
      if (matchKey) {
        myPuppers = this.addressToPuppers[matchKey].tokenIds;
      }
    }
    return myPuppers;
  }

  async refreshDogBalance() {
    try {
      const balance = await this.getDogBalance();
      this.setDogBalance(balance);
    } catch (e) {
      console.error("Failed to refresh DOG balance:", e);
      console.error("Failed to fetch DOG balance");
      this.setDogBalance(BigNumber.from(0)); // set zero if error
    }
  }

  async refreshPupperBalance() {
    const balance = await this.getPupperBalance();
    if (balance >= 0) {
      this.setPupperBalance(balance);
    }
    // On error (-1): leave previous balance in place, don't overwrite with bad data
  }

  async getDogBalance() {
    // log("Checking DOG20 contract initialization:", this.dogContract);
    // log("Current address:", this.address);
    if (!this.dogContract) {
      console.error("DOG20 contract not initialized.");
      return BigNumber.from(0);
    }

    try {
      if (!this.address) return BigNumber.from(0);

      const balance = await this.dogContract.balanceOf(this.address!);
      return balance;
    } catch (error) {
      console.error("Failed to fetch DOG balance:", error);
      return BigNumber.from(0);
    }
  }

  async getPupperBalance() {
    if (!this.address) return 0;

    try {
      const res = await throttledGet(`/v1/px/balance/${this.address}`);
      log('✅ Pupper balance from API:', res.data.balance);
      return res.data.balance;
    } catch (error) {
      console.error('❌ Failed to fetch pupper balance:', error);
      return -1;
    }
  }

  async getPxOwnerByTokenId(tokenId: number) {
    const res = await Http.get(`/v1/px/owner/${tokenId}`);
    return res.data;
  }

  async approvePxSpendDog(amount: BigNumber) {
    return this.dogContract!.approve(this.pxContractAddress, amount);
  }

  async getPxDogSpendAllowance() {
    return this.dogContract!.allowance(this.address!, this.pxContractAddress);
  }

  async getPxLockAmountPerPixel(): Promise<BigNumber> {
    return this.pxContract!.tokenLockAmounts(this.dogContractAddress);
  }

  async getDogToAccount() {
    const freePixelsInDOG = 50;
    //@ts-ignore
    return this.dogContract!.initMock([this.address!], this.DOG_TO_PIXEL_SATOSHIS.mul(freePixelsInDOG));
  }

  async getDogLocked() {
    log(`Fetching locked DOG balance from contract address: ${this.dogContractAddress}`);
    const res = await throttledGet("/v1/dog/locked");
    return res.data.balance;
  }

  // getPercentDogInPixels() {
  //   log(`Fetching percentage of DOG in pixels from contract address: ${this.dogContractAddress}`);
  //   return Http.get<number>("/v1/dog/percentLocked");
  // }

  mintPuppers(pixel_amount: number, forcedGasLimit?: BigNumber) {
    let overrides: any = {};
    if (forcedGasLimit) {
      overrides = { gasLimit: forcedGasLimit };
    }
    // New Foundry contract requires tokenAddress as second parameter
    return this.pxContract!.mintPuppers(pixel_amount, this.dogContractAddress, overrides);
  }

  pupperToPixelCoords(pupper: number) {
    return this.pxContract!.pupperToPixelCoords(pupper);
  }

  burnPupper(pupper: number) {
    return this.pxContract!.burnPuppers([pupper]);
  }

  burnPuppers(puppers: number[]) {
    return this.pxContract!.burnPuppers(puppers);
  }

  // Claim reserved pixels (V1/V2 migration)
  async getReservedTokensForUser(address: string, tokenIdsToCheck: number[]) {
    if (!this.pxContract) {
      throw new Error("PX contract not initialized");
    }
    return this.pxContract.getReservedTokensForUser(address, tokenIdsToCheck);
  }

  async claimReservedToken(tokenId: number) {
    if (!this.pxContract) {
      throw new Error("PX contract not initialized");
    }
    // Use simple claim version (uses default DOG20 token)
    return this.pxContract.claimReservedToken(tokenId);
  }

  async claimReservedTokensBatch(tokenIds: number[]) {
    if (!this.pxContract) {
      throw new Error("PX contract not initialized");
    }
    //@ts-ignore - claimReservedTokensBatch is added in contract update
    return this.pxContract.claimReservedTokensBatch(tokenIds, this.dogContractAddress);
  }

  async canClaimReservedToken(tokenId: number, user: string) {
    if (!this.pxContract) {
      throw new Error("PX contract not initialized");
    }
    return this.pxContract.canClaimReservedToken(tokenId, user);
  }

  async getOwnedEligibleV3Tokens(tokenIds: number[], address: string): Promise<number[]> {
    if (!this.pxContract) return [];
    const results = await Promise.allSettled(
      tokenIds.map(id =>
        this.pxContract!.ownerOf(id).then((owner: string) => ({ id, owner }))
      )
    );
    return results
      .filter(r => r.status === "fulfilled" && (r as PromiseFulfilledResult<{ id: number; owner: string }>).value.owner.toLowerCase() === address.toLowerCase())
      .map(r => (r as PromiseFulfilledResult<{ id: number; owner: string }>).value.id);
  }

  pupperToIndexLocal(pupper: number) {
    return pupper - this.PIXEL_TO_ID_OFFSET;
  }

  pupperToPixelCoordsLocal(pupper: number) {
    const index = this.pupperToIndexLocal(pupper);
    return [index % this.WIDTH, Math.floor(index / this.WIDTH)];
  }

  pupperToHexLocal(pupper: number) {
    const [x, y] = this.pupperToPixelCoordsLocal(pupper);
    return KobosuJson[y][x];
  }

  coordinateToPupperLocal(x: number, y: number) {
    return this.WIDTH * y + x + this.PIXEL_TO_ID_OFFSET;
  }

  isPixelIDValid(id: number) {
    const max = this.WIDTH * this.HEIGHT + this.PIXEL_TO_ID_OFFSET - 1;
    const min = this.PIXEL_TO_ID_OFFSET;
    if (id < min || id > max) {
      return false;
    }
    return true;
  }

  getUSDPerPixel() {
    return Http.get("/v1/px/price").then(({ data }) => {
      this.usdPerPixel = data.price;
    });
  }

  @computed
  get sortedPixelOwners(): PixelOwnerInfo[] {
    const tds = ObjectKeys(this.addressToPuppers).map((key, index, arr) => ({
      address: key,
      ens: this.addressToPuppers![key].ens,
      pixels: this.addressToPuppers![key].tokenIds,
    }));
    return tds
      .filter(dog => dog.address !== ethers.constants.AddressZero)
      .filter(dog => dog.pixels.length > 0)
      .sort((a, b) => {
        if (a.pixels.length > b.pixels.length) {
          return -1;
        } else if (a.pixels.length < b.pixels.length) {
          return 1;
        } else {
          return 0;
        }
      });
  }

  getIsPupperNew(pupper: number) {
    const data = LocalStorage.getItem(VIEWED_PIXELS_LS_KEY, LocalStorage.PARSE_JSON, []);
    let isNew = true;
    if (data.includes(pupper)) {
      isNew = false;
    }
    return isNew;
  }

  setPupperSeen(pupper: number) {
    const data = LocalStorage.getItem(VIEWED_PIXELS_LS_KEY, LocalStorage.PARSE_JSON, []);
    if (!data.includes(pupper)) {
      data.push(pupper);
    }
    LocalStorage.setItem(VIEWED_PIXELS_LS_KEY, data);
  }

  getAddressDisplayName(address: string, shouldAbbreviate = true) {
    if (Object.keys(this.addressToPuppers).includes(address)) {
      const user = this.addressToPuppers[address];
      if (user.ens) {
        return user.ens;
      }
      return shouldAbbreviate ? abbreviate(address, 4) : address;
    } else {
      // @next -- we need to query for ENS or UD here
      return shouldAbbreviate ? abbreviate(address, 4) : address;
    }
  }

  signMessage(message: string) {
    return this.signer.signMessage(message);
  }

  getGlobalTransfers() {
    return Http.post("/v1/transfers", {
      sort: {
        blockNumber: "desc",
      },
    }).then(({ data }) => {
      return data;
    });
  }

  getUserTransfers(address: string) {
    return Http.post(`/v1/transfers/${address}`, {
      sort: {
        blockNumber: "desc",
      },
    }).then(({ data }) => {
      return data;
    });
  }

  /**
   * Get a fresh Web3Provider from the best available source.
   * Prefers the connector transport (the wallet the user actually selected)
   * but falls back to window.ethereum for injected wallets.
   */
  private getFreshProvider(): ethers.providers.Web3Provider {
    const source = this._connectorTransport || (window as any).ethereum;
    if (!source) throw new Error("No wallet provider available");
    return new ethers.providers.Web3Provider(source, 'any');
  }

  // ============================================
  // V1/V2 Legacy Contract Methods for Migration
  // ============================================

  /**
   * Check if current environment is testnet
   */
  get isTestnet(): boolean {
    return this.targetChainId === CHAIN_IDS.BASE_SEPOLIA || this.targetChainId === 1337 || this.targetChainId === 31337;
  }

  /**
   * Get V1 contract address based on environment
   */
  get v1ContractAddress(): string {
    return getV1ContractAddress(this.isTestnet);
  }

  /**
   * Get V2 contract address based on environment
   */
  get v2ContractAddress(): string {
    return getV2ContractAddress(this.isTestnet);
  }

  /**
   * Get V1 chain ID based on environment
   */
  get v1ChainId(): number {
    return getV1ChainId(this.isTestnet);
  }

  /**
   * Get V2 chain ID based on environment
   */
  get v2ChainId(): number {
    return getV2ChainId(this.isTestnet);
  }

  /**
   * Get network display name for a chain ID
   */
  getNetworkDisplayName(chainId: number): string {
    return NETWORK_NAMES[chainId as keyof typeof NETWORK_NAMES] || `Chain ${chainId}`;
  }

  /**
   * Request wallet to switch to a specific network
   */
  async switchNetwork(chainId: number): Promise<boolean> {
    try {
      const source = this._connectorTransport || (window as any).ethereum;
      if (!source?.request) {
        console.error("No wallet provider available for network switch");
        return false;
      }
      await source.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: `0x${chainId.toString(16)}` }],
      });
      return true;
    } catch (error: any) {
      // 4902 = chain not added to wallet
      if (error.code === 4902) {
        log("Chain not added to wallet, need to add it first");
      }
      console.error("Failed to switch network:", error);
      return false;
    }
  }

  /**
   * Get current wallet chain ID
   */
  async getCurrentChainId(): Promise<number | null> {
    try {
      const source = this._connectorTransport || (window as any).ethereum;
      if (!source?.request) return null;
      const chainIdHex = await source.request({ method: "eth_chainId" });
      return chainIdHex ? parseInt(chainIdHex, 16) : null;
    } catch (error) {
      console.error("Failed to get current chain ID:", error);
      return null;
    }
  }

  /**
   * Create a contract instance for V1 (Ethereum Mainnet/Sepolia)
   * Uses the connected wallet's signer
   */
  getV1Contract(): Contract {
    if (!this.signer) {
      throw new Error("Wallet not connected");
    }
    return new Contract(this.v1ContractAddress, LEGACY_PX_ABI, this.signer);
  }

  /**
   * Create a contract instance for V2 (Base/Base Sepolia)
   * Uses the connected wallet's signer
   */
  getV2Contract(): Contract {
    if (!this.signer) {
      throw new Error("Wallet not connected");
    }
    return new Contract(this.v2ContractAddress, LEGACY_PX_ABI, this.signer);
  }

  private parseBurnError(error: any): string {
    const code = error?.code;
    const raw = error?.reason || error?.message || '';
    console.error('Burn error raw:', raw);

    if (code === 'ACTION_REJECTED' || raw.includes('user rejected') || raw.includes('User rejected')) {
      return 'Transaction rejected in wallet';
    }
    if (code === 'INSUFFICIENT_FUNDS' || raw.includes('insufficient funds')) {
      return 'Insufficient ETH for gas fees';
    }
    if (code === 'UNPREDICTABLE_GAS_LIMIT' || raw.includes('execution reverted')) {
      return 'Contract rejected — you may not own these pixels or they are already burned';
    }
    if (code === 'NETWORK_ERROR' || raw.includes('network') || raw.includes('Network')) {
      return 'Network error — check your connection and try again';
    }
    return 'Unknown error — contact the team';
  }

  /**
   * Burn pixels on V1 contract (Ethereum)
   * User must be on Ethereum network
   * @param tokenIds Array of pixel token IDs to burn
   */
  async burnV1Pixels(tokenIds: number[]): Promise<ethers.ContractTransaction> {
    const freshProvider = this.getFreshProvider();
    const network = await freshProvider.getNetwork();
    if (network.chainId !== this.v1ChainId) {
      throw new Error(`Please switch to ${this.getNetworkDisplayName(this.v1ChainId)} to burn V1 pixels`);
    }
    const freshSigner = freshProvider.getSigner();
    const contract = new Contract(this.v1ContractAddress, LEGACY_PX_ABI, freshSigner);
    try {
      return await contract.burnPuppers(tokenIds);
    } catch (error: any) {
      throw new Error(this.parseBurnError(error));
    }
  }

  /**
   * Burn pixels on V2 contract (Base)
   * User must be on Base network
   * @param tokenIds Array of pixel token IDs to burn
   */
  async burnV2Pixels(tokenIds: number[]): Promise<ethers.ContractTransaction> {
    const freshProvider = this.getFreshProvider();
    const network = await freshProvider.getNetwork();
    if (network.chainId !== this.v2ChainId) {
      throw new Error(`Please switch to ${this.getNetworkDisplayName(this.v2ChainId)} to burn V2 pixels`);
    }
    const freshSigner = freshProvider.getSigner();
    const contract = new Contract(this.v2ContractAddress, LEGACY_PX_ABI, freshSigner);
    try {
      return await contract.burnPuppers(tokenIds);
    } catch (error: any) {
      throw new Error(this.parseBurnError(error));
    }
  }

  /**
   * Fetch migration eligibility from server
   * Returns pixel IDs grouped by network that user is eligible to claim
   */
  async getMigrationEligibility(address: string): Promise<{ mainnet: number[]; base: number[] }> {
    try {
      const response = await Http.get(`/v1/migration/eligible/${address}`);
      const data = response.data;
      return {
        mainnet: data.mainnet || [],
        base: [...(data.base || []), ...(data['base-sepolia'] || [])],
      };
    } catch (error) {
      console.error("Failed to fetch migration eligibility:", error);
      // Return empty if endpoint not available yet
      return { mainnet: [], base: [] };
    }
  }
}

export default Web3Store;
